/**
 * How the bento's two headline figures are sized. Kept out of `stat-tiles.tsx` so the budget below can
 * be tested without importing React, the way `image-sizing.ts` is.
 */

/**
 * The figure's width in em, and so the divisor the tile's width is scaled by.
 *
 * Measured, not guessed: the figure renders 0.478em per character in Rethink Sans at `tracking-tight`
 * (production's "24.753.869,99 €" is 344px at 48px), and 7.8 covers sixteen of them with about 2% of
 * margin. Dividing the tile's width by a CONSTANT is what keeps the counter still — the type size
 * follows the tile and never the digits, so a figure that ticks cannot resize itself.
 *
 * It was 6.75, budgeted for fourteen characters, because that is what the DEV total is — 1.356.777,57 €.
 * Production had already passed twenty million, so the live figure was fifteen characters and wrapped
 * onto three lines. Calibrate this against production, never against the seed data.
 */
export const FIGURE_EM = 7.8;

/**
 * The longest figure this budget covers, in characters. "999.999.999,99 €" is sixteen, so the tile holds
 * every total up to a billion euros — the euro separators fall in fixed places and tabular digits all
 * share one advance, so each magnitude renders exactly one width.
 *
 * Sixteen rather than the fifteen production needs today: a headline number that grows past its own
 * layout is precisely the fault this constant exists to catch, and the last budget was overtaken in
 * production before it ever shipped to dev's eyes. `stat-figure.unit.test.ts` fails when the budget can
 * no longer hold this, rather than letting the tile quietly wrap or truncate the figure.
 */
export const MAX_FIGURE_CHARS = 16;

/**
 * The class the two figures wear. The `md:` rule sizes the type from the TILE (`cqi`) rather than the
 * viewport, because the bento is `max-w-[1200px]` — past 1280 the columns stop growing while the
 * viewport keeps going, so a `vw` rule would enlarge type into a box that is done changing size.
 * `min()` keeps the hand-off's 3rem as the ceiling, so wide screens are untouched and only the cramped
 * widths give way.
 *
 * `tracking-tight` is here rather than on the figure because the two renderings of it differ — the live
 * counter is `RollingFigure`'s row of columns, the Suspense fallback a bare string — and a tracking that
 * only one of them carried made the fallback render 5% wider than the counter that replaces it, which is
 * the one thing a fallback exists not to do.
 *
 * `whitespace-nowrap` is the backstop, not the fix. If a future total outgrows the budget above, the
 * figure should overflow its tile rather than reflow onto a second line: an overflow is a visual defect
 * someone reports, while a wrap silently changes the tile's HEIGHT and shoves the bento's rows around.
 *
 * The divisor is written out because Tailwind only sees literal class strings; the test pins it to
 * `FIGURE_EM` so the two cannot drift apart.
 */
export const FLUID_FIGURE = 'tracking-tight text-3xl whitespace-nowrap md:[font-size:min(3rem,calc(100cqi/7.8))]';
